import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  ArrowLeft,
  Upload,
  FileText,
  Building2,
  FolderOpen,
  Download,
  Trash2,
  Eye,
  Search,
  Filter,
  ChevronDown,
  ChevronRight,
  X,
  Loader2,
  CheckCircle,
  AlertCircle,
  Home,
  FileType,
  Sparkles,
  Link as LinkIcon,
  Menu
} from "lucide-react";
import { authGet, authPost, authDelete, authPut } from "@/utils/auth";

import MobileBottomNav from "@/components/MobileBottomNav";
import MobileSidebar from "@/components/MobileSidebar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Document type colors
const DOCUMENT_TYPE_COLORS = {
  "Policy Wordings": "bg-[#EDF1F8]/40 text-[#2E3B57] border-blue-500/30",
  "Rider Information": "bg-purple-500/20 text-purple-600 border-purple-500/30",
  "Customer Information Sheet": "bg-emerald-500/20 text-emerald-600 border-emerald-500/30",
  "Brochure": "bg-[#D4A017]/15 text-[#A87608] border-[#D4A017]/30",
  "Prospectus": "bg-cyan-500/20 text-cyan-600 border-cyan-500/30",
  "Claim Form": "bg-rose-500/20 text-rose-600 border-rose-500/30",
  "Other": "bg-[#EDF1F8]/20 text-[#3A4557] border-[#C9D4E6]/30"
};

const DOCUMENT_TYPES = [
  "Policy Wordings",
  "Rider Information",
  "Customer Information Sheet",
  "Brochure",
  "Prospectus",
  "Claim Form",
  "Other"
];

export default function DocumentLibrary() {
  const navigate = useNavigate();
  const [documents, setDocuments] = useState([]);
  const [allPlans, setAllPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [filterType, setFilterType] = useState("all");
  const [expandedCompanies, setExpandedCompanies] = useState({});
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [selectedDocument, setSelectedDocument] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);

  // Upload form state
  const [uploadFiles, setUploadFiles] = useState([]);
  const [manualCompany, setManualCompany] = useState("");
  const [manualDocType, setManualDocType] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("");
  const [uploadNotes, setUploadNotes] = useState("");
  const [uploadResults, setUploadResults] = useState([]);
  const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
  const [showSidebar, setShowSidebar] = useState(false);

  // Fetch documents and plans
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [docsResponse, plansResponse] = await Promise.all([
        authGet(`${API}/advisor/documents`),
        fetch(`${API}/plans`).then(r => r.json())
      ]);
      
      // authGet returns { ok, status, data } - we need docsResponse.data
      const docsData = docsResponse.data || docsResponse;
      setDocuments(docsData.documents || []);
      setAllPlans(plansResponse || []);
      
      // Auto-expand all companies
      const expanded = {};
      (docsData.documents || []).forEach(company => {
        expanded[company.company_id] = true;
      });
      setExpandedCompanies(expanded);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Handle file upload - supports multiple files
  const handleFileUpload = async (e) => {
    e.preventDefault();
    if (uploadFiles.length === 0) return;

    setUploading(true);
    setUploadResults([]);
    setCurrentUploadIndex(0);

    const results = [];
    const totalFiles = uploadFiles.length;

    for (let i = 0; i < uploadFiles.length; i++) {
      const file = uploadFiles[i];
      setCurrentUploadIndex(i + 1);
      setUploadProgress(`Uploading file ${i + 1} of ${totalFiles}: ${file.name}...`);

      try {
        const formData = new FormData();
        formData.append("file", file);
        if (manualCompany) formData.append("company_name", manualCompany);
        if (manualDocType) formData.append("document_type", manualDocType);
        if (selectedPlanId) formData.append("plan_id", selectedPlanId);
        if (uploadNotes) formData.append("notes", uploadNotes);

        setUploadProgress(`Analyzing file ${i + 1} of ${totalFiles} with AI: ${file.name}...`);

        const response = await fetch(`${API}/advisor/documents/upload`, {
          method: "POST",
          headers: {
            "Authorization": `Bearer ${localStorage.getItem("admin_token") || localStorage.getItem("employee_token")}`
          },
          body: formData
        });

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        const result = await response.json();
        results.push({
          filename: file.name,
          success: true,
          company: result.document?.company_name || result.ai_analysis?.company_name || "Unknown",
          docType: result.document?.document_type || result.ai_analysis?.document_type || "Other"
        });
      } catch (error) {
        console.error(`Upload error for ${file.name}:`, error);
        results.push({
          filename: file.name,
          success: false,
          error: error.message
        });
      }
    }

    setUploadResults(results);
    const successCount = results.filter(r => r.success).length;
    setUploadProgress(`Completed: ${successCount} of ${totalFiles} files uploaded successfully!`);

    // Refresh documents list
    await fetchData();

    // Reset form after delay
    setTimeout(() => {
      setShowUploadModal(false);
      setUploadFiles([]);
      setManualCompany("");
      setManualDocType("");
      setSelectedPlanId("");
      setUploadNotes("");
      setUploadProgress(null);
      setUploadResults([]);
      setCurrentUploadIndex(0);
    }, 3000);

    setUploading(false);
  };

  // Handle document delete
  const handleDelete = async (documentId) => {
    if (!window.confirm("Are you sure you want to delete this document?")) return;

    try {
      await authDelete(`${API}/advisor/documents/${documentId}`);
      await fetchData();
    } catch (error) {
      console.error("Delete error:", error);
      alert("Failed to delete document");
    }
  };

  // Handle document download
  const handleDownload = async (doc) => {
    try {
      const token = localStorage.getItem("admin_token") || localStorage.getItem("employee_token");
      const response = await fetch(`${API}/advisor/documents/${doc.id}/download?auth=${token}`);
      
      if (!response.ok) throw new Error("Download failed");
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = doc.original_filename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert("Failed to download document");
    }
  };

  // Handle document update
  const handleUpdateDocument = async (e) => {
    e.preventDefault();
    if (!selectedDocument) return;

    try {
      await authPut(`${API}/advisor/documents/${selectedDocument.id}`, {
        company_name: selectedDocument.company_name,
        company_id: selectedDocument.company_id,
        document_type: selectedDocument.document_type,
        plan_id: selectedDocument.plan_id,
        plan_name: selectedDocument.plan_name,
        notes: selectedDocument.notes
      });
      
      await fetchData();
      setShowEditModal(false);
      setSelectedDocument(null);
    } catch (error) {
      console.error("Update error:", error);
      alert("Failed to update document");
    }
  };

  // Filter documents
  const filteredDocuments = documents.filter(company => {
    const companyDocs = company.documents.filter(doc => {
      const matchesSearch = searchQuery === "" || 
        doc.original_filename.toLowerCase().includes(searchQuery.toLowerCase()) ||
        doc.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (doc.plan_name && doc.plan_name.toLowerCase().includes(searchQuery.toLowerCase()));
      
      const matchesType = filterType === "all" || doc.document_type === filterType;
      
      return matchesSearch && matchesType;
    });
    return companyDocs.length > 0;
  });

  // Toggle company expansion
  const toggleCompany = (companyId) => {
    setExpandedCompanies(prev => ({
      ...prev,
      [companyId]: !prev[companyId]
    }));
  };

  // Format file size
  const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F4F6FA] flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-[#A87608] mx-auto mb-4" />
          <p className="text-[#3A4557]">Loading documents...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F4F6FA] text-[#1B2A4A] pb-24 md:pb-0">
      {/* Mobile Sidebar */}
      <MobileSidebar isOpen={showSidebar} onClose={() => setShowSidebar(false)} />

      {/* Mobile Header */}
      <header className="md:hidden sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowSidebar(true)}
              className="p-2 -ml-2 rounded-xl bg-[#EDF1F8] hover:bg-[#E2E9F4] active:bg-[#E2E9F4] transition-colors"
            >
              <Menu className="h-5 w-5 text-[#1B2A4A]" />
            </button>
            <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
              <FolderOpen className="h-5 w-5 text-[#1B2A4A]" />
            </div>
            <div>
              <h1 className="text-lg font-bold">Documents</h1>
              <p className="text-xs text-[#3A4557]">{documents.reduce((acc, c) => acc + c.documents.length, 0)} files</p>
            </div>
          </div>
          
          <button
            onClick={() => setShowUploadModal(true)}
            className="p-2.5 rounded-xl bg-gradient-to-r from-[#D4A017] to-[#D9A82C] active:scale-95 transition-transform"
          >
            <Upload className="h-5 w-5 text-[#1B2A4A]" />
          </button>
        </div>
        
        {/* Mobile Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50 text-sm"
            />
          </div>
        </div>
      </header>

      {/* Desktop Header */}
      <header className="hidden md:block sticky top-0 z-40 bg-[#F4F6FA]/95 backdrop-blur-sm border-b border-[#C9D4E6]">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate(-1)}
              className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
            >
              <ArrowLeft className="h-5 w-5" />
            </button>
            <button
              onClick={() => navigate("/advisor/dashboard")}
              className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
            >
              <Home className="h-5 w-5" />
            </button>
            <div className="flex items-center gap-2">
              <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                <FolderOpen className="h-5 w-5 text-[#1B2A4A]" />
              </div>
              <div>
                <h1 className="text-lg font-bold">Document Library</h1>
                <p className="text-xs text-[#3A4557]">Insurance Plan Documents</p>
              </div>
            </div>
          </div>
          
          <Button
            onClick={() => setShowUploadModal(true)}
            className="bg-gradient-to-r from-[#D4A017] to-[#D9A82C] hover:from-[#D4A017] hover:to-[#8A5A12] text-white"
          >
            <Upload className="h-4 w-4 mr-2" />
            Upload Document
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-4 md:py-6">
        {/* Search and Filter - Desktop Only */}
        <div className="hidden md:flex flex-col sm:flex-row gap-4 mb-6">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <input
              type="text"
              placeholder="Search documents..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
            />
          </div>
          
          <div className="relative">
            <Filter className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557]" />
            <select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              className="pl-10 pr-8 py-2.5 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50 appearance-none cursor-pointer"
            >
              <option value="all">All Document Types</option>
              {DOCUMENT_TYPES.map(type => (
                <option key={type} value={type}>{type}</option>
              ))}
            </select>
            <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#3A4557] pointer-events-none" />
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#EDF1F8]/40 flex items-center justify-center">
                <Building2 className="h-5 w-5 text-[#2E3B57]" />
              </div>
              <div>
                <p className="text-2xl font-bold">{documents.length}</p>
                <p className="text-xs text-[#3A4557]">Companies</p>
              </div>
            </div>
          </div>
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-[#D4A017]/15 flex items-center justify-center">
                <FileText className="h-5 w-5 text-[#A87608]" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {documents.reduce((acc, c) => acc + c.documents.length, 0)}
                </p>
                <p className="text-xs text-[#3A4557]">Documents</p>
              </div>
            </div>
          </div>
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-emerald-500/20 flex items-center justify-center">
                <LinkIcon className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">
                  {documents.reduce((acc, c) => acc + c.documents.filter(d => d.plan_id).length, 0)}
                </p>
                <p className="text-xs text-[#3A4557]">Linked to Plans</p>
              </div>
            </div>
          </div>
          <div className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl p-4">
            <div className="flex items-center gap-3">
              <div className="h-10 w-10 rounded-lg bg-purple-500/20 flex items-center justify-center">
                <FileType className="h-5 w-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold">{DOCUMENT_TYPES.length}</p>
                <p className="text-xs text-[#3A4557]">Doc Types</p>
              </div>
            </div>
          </div>
        </div>

        {/* Documents by Company */}
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-16 bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl">
            <FolderOpen className="h-16 w-16 text-[#3A4557] mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-[#3A4557] mb-2">No Documents Found</h3>
            <p className="text-[#3A4557] mb-6">Upload your first insurance document to get started</p>
            <Button
              onClick={() => setShowUploadModal(true)}
              className="bg-gradient-to-r from-[#D4A017] to-[#D9A82C]"
            >
              <Upload className="h-4 w-4 mr-2" />
              Upload Document
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredDocuments.map(company => (
              <motion.div
                key={company.company_id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-[#EDF1F8]/30 border border-[#C9D4E6]/50 rounded-xl overflow-hidden"
              >
                {/* Company Header */}
                <button
                  onClick={() => toggleCompany(company.company_id)}
                  className="w-full flex items-center justify-between p-4 hover:bg-[#E2E9F4]/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-[#EDF1F8] to-indigo-600 flex items-center justify-center">
                      <Building2 className="h-5 w-5 text-[#1B2A4A]" />
                    </div>
                    <div className="text-left">
                      <h3 className="font-semibold text-[#1B2A4A]">{company.company_name}</h3>
                      <p className="text-xs text-[#3A4557]">
                        {company.documents.filter(d => {
                          const matchesSearch = searchQuery === "" || 
                            d.original_filename.toLowerCase().includes(searchQuery.toLowerCase());
                          const matchesType = filterType === "all" || d.document_type === filterType;
                          return matchesSearch && matchesType;
                        }).length} documents
                      </p>
                    </div>
                  </div>
                  {expandedCompanies[company.company_id] ? (
                    <ChevronDown className="h-5 w-5 text-[#3A4557]" />
                  ) : (
                    <ChevronRight className="h-5 w-5 text-[#3A4557]" />
                  )}
                </button>

                {/* Documents List */}
                <AnimatePresence>
                  {expandedCompanies[company.company_id] && (
                    <motion.div
                      initial={{ height: 0, opacity: 0 }}
                      animate={{ height: "auto", opacity: 1 }}
                      exit={{ height: 0, opacity: 0 }}
                      transition={{ duration: 0.2 }}
                      className="border-t border-[#C9D4E6]/50"
                    >
                      <div className="p-4 space-y-3">
                        {company.documents
                          .filter(doc => {
                            const matchesSearch = searchQuery === "" || 
                              doc.original_filename.toLowerCase().includes(searchQuery.toLowerCase());
                            const matchesType = filterType === "all" || doc.document_type === filterType;
                            return matchesSearch && matchesType;
                          })
                          .map(doc => (
                            <div
                              key={doc.id}
                              className="flex items-center justify-between p-3 bg-white/50 rounded-lg border border-[#C9D4E6]/30 hover:border-[#C9D4E6] transition-colors"
                            >
                              <div className="flex items-center gap-3 flex-1 min-w-0">
                                <div className="h-10 w-10 rounded-lg bg-[#E2E9F4]/50 flex items-center justify-center flex-shrink-0">
                                  <FileText className="h-5 w-5 text-[#3A4557]" />
                                </div>
                                <div className="min-w-0 flex-1">
                                  <p className="font-medium text-[#1B2A4A] truncate">{doc.original_filename}</p>
                                  <div className="flex items-center gap-2 flex-wrap mt-1">
                                    <span className={`px-2 py-0.5 rounded-full text-xs border ${DOCUMENT_TYPE_COLORS[doc.document_type] || DOCUMENT_TYPE_COLORS["Other"]}`}>
                                      {doc.document_type}
                                    </span>
                                    <span className="text-xs text-[#3A4557]">{formatSize(doc.size)}</span>
                                    {doc.plan_name && (
                                      <span className="text-xs text-emerald-600 flex items-center gap-1">
                                        <LinkIcon className="h-3 w-3" />
                                        {doc.plan_name}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              <div className="flex items-center gap-2 ml-4">
                                <button
                                  onClick={() => handleDownload(doc)}
                                  className="p-2 hover:bg-[#E2E9F4] rounded-lg transition-colors text-[#3A4557] hover:text-[#1B2A4A]"
                                  title="Download"
                                >
                                  <Download className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => {
                                    setSelectedDocument(doc);
                                    setShowEditModal(true);
                                  }}
                                  className="p-2 hover:bg-[#E2E9F4] rounded-lg transition-colors text-[#3A4557] hover:text-[#1B2A4A]"
                                  title="Edit"
                                >
                                  <Eye className="h-4 w-4" />
                                </button>
                                <button
                                  onClick={() => handleDelete(doc.id)}
                                  className="p-2 hover:bg-red-500/20 rounded-lg transition-colors text-[#3A4557] hover:text-red-600"
                                  title="Delete"
                                >
                                  <Trash2 className="h-4 w-4" />
                                </button>
                              </div>
                            </div>
                          ))}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </motion.div>
            ))}
          </div>
        )}
      </main>

      {/* Upload Modal */}
      <AnimatePresence>
        {showUploadModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => !uploading && setShowUploadModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white border border-[#C9D4E6] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[#C9D4E6] flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-[#D4A017] to-[#D9A82C] flex items-center justify-center">
                    <Upload className="h-5 w-5 text-[#1B2A4A]" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold">Upload Document</h2>
                    <p className="text-xs text-[#3A4557]">AI will auto-categorize your document</p>
                  </div>
                </div>
                <button
                  onClick={() => !uploading && setShowUploadModal(false)}
                  disabled={uploading}
                  className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleFileUpload} className="p-6 space-y-5">
                {/* File Upload - Multiple Files */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Select Files (PDF or Images) *
                  </label>
                  <div className="relative">
                    <input
                      type="file"
                      accept=".pdf,image/*"
                      multiple
                      onChange={(e) => setUploadFiles(Array.from(e.target.files))}
                      disabled={uploading}
                      className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-[#D4A017] file:text-[#1B2A4A] file:cursor-pointer hover:file:bg-[#D4A017]"
                    />
                  </div>
                  {uploadFiles.length > 0 && (
                    <div className="mt-3 space-y-2">
                      <p className="text-xs text-emerald-600 flex items-center gap-1">
                        <CheckCircle className="h-3 w-3" />
                        {uploadFiles.length} file(s) selected
                      </p>
                      <div className="max-h-32 overflow-y-auto space-y-1">
                        {uploadFiles.map((file, idx) => (
                          <div key={idx} className="flex items-center justify-between text-xs bg-[#EDF1F8]/50 rounded-lg px-3 py-2">
                            <span className="text-[#2E3B57] truncate flex-1">{file.name}</span>
                            <span className="text-[#3A4557] ml-2">{formatSize(file.size)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                {/* AI Analysis Info */}
                <div className="p-4 bg-gradient-to-r from-purple-500/10 to-[#DCE4F2]/10 border border-purple-500/30 rounded-xl">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-purple-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="text-sm text-purple-700 font-medium">AI-Powered Categorization</p>
                      <p className="text-xs text-[#3A4557] mt-1">
                        Upload documents and AI will automatically detect the insurance company and document type for each file. You can set defaults below.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Manual Override - Company */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Company Name (Optional - AI will detect)
                  </label>
                  <select
                    value={manualCompany}
                    onChange={(e) => { setManualCompany(e.target.value); setSelectedPlanId(""); }}
                    disabled={uploading}
                    className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                    data-testid="upload-company-dropdown"
                  >
                    <option value="">Auto-detect with AI</option>
                    {[...new Set(allPlans.map(p => p.company))].sort().map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>

                {/* Link to Plan */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Plan (Optional)
                  </label>
                  <select
                    value={selectedPlanId}
                    onChange={(e) => setSelectedPlanId(e.target.value)}
                    disabled={uploading}
                    className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                    data-testid="upload-plan-dropdown"
                  >
                    <option value="">Select Plan</option>
                    {allPlans
                      .filter(p => !manualCompany || p.company === manualCompany)
                      .map(plan => (
                        <option key={plan.id} value={plan.id}>{plan.name}</option>
                      ))
                    }
                  </select>
                </div>

                {/* Manual Override - Document Type */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Notes (Optional)
                  </label>
                  <textarea
                    value={uploadNotes}
                    onChange={(e) => setUploadNotes(e.target.value)}
                    placeholder="Any additional notes about this document..."
                    disabled={uploading}
                    rows={2}
                    className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50 resize-none"
                  />
                </div>

                {/* Upload Progress */}
                {uploadProgress && (
                  <div className={`p-4 rounded-xl flex items-center gap-3 ${
                    uploadProgress.includes("failed") 
                      ? "bg-red-500/10 border border-red-500/30" 
                      : uploadProgress.includes("success")
                        ? "bg-emerald-500/10 border border-emerald-500/30"
                        : "bg-[#EDF1F8]/30 border border-blue-500/30"
                  }`}>
                    {uploadProgress.includes("failed") ? (
                      <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0" />
                    ) : uploadProgress.includes("success") ? (
                      <CheckCircle className="h-5 w-5 text-emerald-600 flex-shrink-0" />
                    ) : (
                      <Loader2 className="h-5 w-5 text-[#2E3B57] animate-spin flex-shrink-0" />
                    )}
                    <div className="flex-1">
                      <p className={`text-sm font-medium ${
                        uploadProgress.includes("failed") 
                          ? "text-red-600" 
                          : uploadProgress.includes("Completed")
                            ? "text-emerald-600"
                            : "text-[#2E3B57]"
                      }`}>
                        {uploadProgress}
                      </p>
                      {uploading && uploadFiles.length > 1 && (
                        <div className="mt-2">
                          <div className="h-1.5 bg-[#E2E9F4] rounded-full overflow-hidden">
                            <div 
                              className="h-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C] transition-all duration-300"
                              style={{ width: `${(currentUploadIndex / uploadFiles.length) * 100}%` }}
                            />
                          </div>
                          <p className="text-xs text-[#3A4557] mt-1">
                            {currentUploadIndex} of {uploadFiles.length} files
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Upload Results */}
                {uploadResults.length > 0 && (
                  <div className="max-h-40 overflow-y-auto space-y-2">
                    {uploadResults.map((result, idx) => (
                      <div 
                        key={idx} 
                        className={`flex items-center gap-2 p-2 rounded-lg text-xs ${
                          result.success 
                            ? "bg-emerald-500/10 border border-emerald-500/30" 
                            : "bg-red-500/10 border border-red-500/30"
                        }`}
                      >
                        {result.success ? (
                          <CheckCircle className="h-4 w-4 text-emerald-600 flex-shrink-0" />
                        ) : (
                          <AlertCircle className="h-4 w-4 text-red-600 flex-shrink-0" />
                        )}
                        <span className={`truncate flex-1 ${result.success ? "text-emerald-700" : "text-red-700"}`}>
                          {result.filename}
                        </span>
                        {result.success && (
                          <span className="text-[#3A4557]">{result.company} - {result.docType}</span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Submit Button */}
                <Button
                  type="submit"
                  disabled={uploadFiles.length === 0 || uploading}
                  className="w-full bg-gradient-to-r from-[#D4A017] to-[#D9A82C] hover:from-[#D4A017] hover:to-[#8A5A12] disabled:opacity-50"
                >
                  {uploading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Processing {currentUploadIndex} of {uploadFiles.length}...
                    </>
                  ) : (
                    <>
                      <Upload className="h-4 w-4 mr-2" />
                      Upload {uploadFiles.length > 0 ? `${uploadFiles.length} File${uploadFiles.length > 1 ? 's' : ''}` : '& Analyze'}
                    </>
                  )}
                </Button>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Edit Modal */}
      <AnimatePresence>
        {showEditModal && selectedDocument && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4"
            onClick={() => setShowEditModal(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-white border border-[#C9D4E6] rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
            >
              <div className="p-6 border-b border-[#C9D4E6] flex items-center justify-between">
                <h2 className="text-lg font-bold">Edit Document</h2>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="p-2 hover:bg-[#EDF1F8] rounded-lg transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <form onSubmit={handleUpdateDocument} className="p-6 space-y-5">
                {/* File Info */}
                <div className="p-4 bg-[#EDF1F8]/50 rounded-xl">
                  <p className="text-sm text-[#3A4557]">File</p>
                  <p className="font-medium truncate">{selectedDocument.original_filename}</p>
                  <p className="text-xs text-[#3A4557] mt-1">{formatSize(selectedDocument.size)}</p>
                </div>

                {/* Company Name - Dropdown */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Company Name
                  </label>
                  <select
                    value={selectedDocument.company_name || ""}
                    onChange={(e) => {
                      const companyName = e.target.value;
                      const matchedPlan = allPlans.find(p => p.company === companyName);
                      setSelectedDocument({
                        ...selectedDocument,
                        company_name: companyName,
                        company_id: matchedPlan ? matchedPlan.company_id || companyName.toLowerCase().replace(/\s+/g, '-') : selectedDocument.company_id,
                        plan_id: "", // reset plan when company changes
                        plan_name: ""
                      });
                    }}
                    className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                    data-testid="edit-company-dropdown"
                  >
                    <option value="">Select Company</option>
                    {[...new Set(allPlans.map(p => p.company))].sort().map(company => (
                      <option key={company} value={company}>{company}</option>
                    ))}
                  </select>
                </div>

                {/* Plan Name - Dropdown filtered by company */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Plan
                  </label>
                  <select
                    value={selectedDocument.plan_id || ""}
                    onChange={(e) => {
                      const planId = e.target.value;
                      const plan = allPlans.find(p => p.id === planId);
                      setSelectedDocument({
                        ...selectedDocument,
                        plan_id: planId,
                        plan_name: plan ? plan.name : ""
                      });
                    }}
                    className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                    data-testid="edit-plan-dropdown"
                  >
                    <option value="">Select Plan</option>
                    {allPlans
                      .filter(p => !selectedDocument.company_name || p.company === selectedDocument.company_name)
                      .map(plan => (
                        <option key={plan.id} value={plan.id}>{plan.name}</option>
                      ))
                    }
                  </select>
                </div>

                {/* Document Type */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Document Type
                  </label>
                  <select
                    value={selectedDocument.document_type}
                    onChange={(e) => setSelectedDocument({...selectedDocument, document_type: e.target.value})}
                    className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50"
                  >
                    {DOCUMENT_TYPES.map(type => (
                      <option key={type} value={type}>{type}</option>
                    ))}
                  </select>
                </div>

                {/* Notes */}
                <div>
                  <label className="block text-sm font-medium text-[#2E3B57] mb-2">
                    Notes
                  </label>
                  <textarea
                    value={selectedDocument.notes || ""}
                    onChange={(e) => setSelectedDocument({...selectedDocument, notes: e.target.value})}
                    placeholder="Any additional notes..."
                    rows={2}
                    className="w-full p-3 bg-[#EDF1F8]/50 border border-[#C9D4E6] rounded-xl text-[#1B2A4A] placeholder-[#9AA3B2] focus:outline-none focus:ring-2 focus:ring-[#D4A017]/50 resize-none"
                  />
                </div>

                {/* AI Analysis Info */}
                {selectedDocument.ai_analysis && !selectedDocument.ai_analysis.error && (
                  <div className="p-4 bg-purple-500/10 border border-purple-500/30 rounded-xl">
                    <p className="text-sm text-purple-700 font-medium mb-2">AI Analysis</p>
                    <p className="text-xs text-[#3A4557]">
                      Detected: {selectedDocument.ai_analysis.company_name} - {selectedDocument.ai_analysis.document_type}
                    </p>
                    {selectedDocument.ai_analysis.summary && (
                      <p className="text-xs text-[#3A4557] mt-1">{selectedDocument.ai_analysis.summary}</p>
                    )}
                  </div>
                )}

                {/* Actions */}
                <div className="flex gap-3">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => handleDownload(selectedDocument)}
                    className="flex-1 border-[#C9D4E6] text-[#2E3B57] hover:bg-[#EDF1F8]"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download
                  </Button>
                  <Button
                    type="submit"
                    className="flex-1 bg-gradient-to-r from-[#D4A017] to-[#D9A82C]"
                  >
                    Save Changes
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile Bottom Navigation */}
      <MobileBottomNav onMenuClick={() => setShowSidebar(true)} />
    </div>
  );
}
